import { isPlatformServer, PlatformLocation } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, isDevMode, PLATFORM_ID } from '@angular/core';
import { ConfigService } from '@gallaghercp/core-frontend';
import { Observable } from 'rxjs';

/**
 * The service gateway resolves the tenant from `x-forwarded-host`. In the browser that header is
 * added by the proxy layer (`src/proxy.conf.json` in dev), but server-side renders call the gateway
 * directly, so the header has to be attached here or the gateway only ever sees the seed tenant.
 */
@Injectable()
export class ForwardedHostInterceptor implements HttpInterceptor {
    private readonly isServer = isPlatformServer(inject(PLATFORM_ID));
    private readonly platformLocation = inject(PlatformLocation);
    private readonly config = inject(ConfigService);

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const api = this.config.get('api');
        if (!this.isServer || !api || !request.url.startsWith(api)) {
            return next.handle(request);
        }

        const host = resolveRequestHostname(this.platformLocation, this.config.get('frontend') ?? undefined);
        if (!host) {
            return next.handle(request);
        }

        return next.handle(request.clone({ setHeaders: { 'x-forwarded-host': host } }));
    }
}

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

/**
 * The hostname to resolve the tenant from. Angular's server platform seeds `PlatformLocation` from
 * the render URL, which the server layer builds out of the incoming request.
 *
 * A development server is reached over `localhost`, a LAN address or a `.local` name — none of which
 * the gateway can map to a tenant — so development always forwards the configured frontend host, the
 * same substitution `proxy.conf.json` makes for browser requests. A production build keeps using the
 * request's own host, falling back for loopback addresses so it can still be smoke-tested locally.
 */
export function resolveRequestHostname(platformLocation: PlatformLocation, frontendHost?: string): string | null {
    if (isDevMode()) {
        return configuredHostname(frontendHost);
    }

    const hostname = platformLocation.hostname;
    return hostname && !LOOPBACK_HOSTNAMES.has(hostname) ? hostname : configuredHostname(frontendHost);
}

function configuredHostname(frontendHost?: string): string | null {
    try {
        return new URL(frontendHost ?? '').hostname || null;
    } catch {
        return null;
    }
}
